import React, { Suspense, useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";
import useScrollLock from "../../../../../../hooks/useScrollLock";
import { MediaRecord } from "../../../../../../interfaces/MediaObject";
import mediaUtility from "../../../../../../services/utility/MediaUtility";
// pdfjs-dist is ~1 MB. Load it only when the opened media is actually a
// PDF, so a QR visitor browsing a business gallery never pays for it.
const PdfCanvasViewer = React.lazy(() => import("./PdfCanvasViewer"));
import "./styles/MediaViewer.css";

interface MediaViewerProps {
  items: MediaRecord[];
  initialIndex: number;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

/**
 * Full-screen viewer for mixed gallery media. Renders an image, a playable
 * video, or an embedded PDF (with an "open in new tab" fallback) and supports
 * keyboard / arrow navigation across the whole set.
 */
const MediaViewer: React.FC<MediaViewerProps> = ({ items, initialIndex, onClose, onIndexChange }) => {
  const [index, setIndex] = useState(initialIndex);

  useEffect(() => {
    setIndex(initialIndex);
  }, [initialIndex]);

  useScrollLock(true);

  const count = items.length;
  const prev = useCallback(() => {
    const newIdx = (index - 1 + count) % count;
    setIndex(newIdx);
    onIndexChange?.(newIdx);
  }, [count, index, onIndexChange]);

  const next = useCallback(() => {
    const newIdx = (index + 1) % count;
    setIndex(newIdx);
    onIndexChange?.(newIdx);
  }, [count, index, onIndexChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, prev, next]);

  const current = items[index];
  if (!current) return null;
  const kind = mediaUtility.kindFromContentType(current.mediaType);

  // Portalled to document.body — same reason MorphCategoryPanel is: a
  // fixed-position overlay nested wherever the caller happens to render it
  // (e.g. inside Gallery's open-album panel, itself a portal at z-index
  // 800+) can still end up UNDER a sibling overlay that has a higher
  // z-index, since z-index only resolves within a shared stacking context.
  // Portalling to <body> plus a z-index above the category-morph panel
  // (see MediaViewer.css) guarantees this always ends up on top, however
  // deep the tap that opened it was.
  return createPortal(
    <div className="media-viewer" onClick={onClose}>
      <button className="media-viewer__close" onClick={onClose} aria-label="Close">
        <X size={22} />
      </button>

      {count > 1 && (
        <button
          className="media-viewer__nav media-viewer__nav--prev"
          onClick={(e) => {
            e.stopPropagation();
            prev();
          }}
          aria-label="Previous item"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      <div className="media-viewer__stage" onClick={(e) => e.stopPropagation()}>
        {kind === "image" && (
          <img className="media-viewer__img" src={current.publicUrl} alt={`Gallery item ${index + 1}`} />
        )}

        {kind === "video" && (
          <video className="media-viewer__video" src={current.publicUrl} controls autoPlay playsInline />
        )}

        {kind === "pdf" && (
          <div className="media-viewer__pdf">
            {/* Canvas render via pdf.js, not an <iframe> — the wrapped
             * Android/iOS app has no built-in PDF viewer to hand an
             * iframed PDF to, so it was getting kicked out to an external
             * app instead. See PdfCanvasViewer's doc comment. The link
             * below stays as an explicit escape hatch to the OS viewer. */}
            <Suspense fallback={<div className="media-viewer__pdf-loading">Loading PDF…</div>}>
              <PdfCanvasViewer
                url={current.publicUrl}
                fileName={mediaUtility.fileNameFromPath(current.objectPath)}
              />
            </Suspense>
            <a
              className="media-viewer__pdf-open"
              href={current.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={16} />
              Open PDF in new tab
            </a>
          </div>
        )}
      </div>

      {count > 1 && (
        <button
          className="media-viewer__nav media-viewer__nav--next"
          onClick={(e) => {
            e.stopPropagation();
            next();
          }}
          aria-label="Next item"
        >
          <ChevronRight size={26} />
        </button>
      )}

      <div className="media-viewer__counter" onClick={(e) => e.stopPropagation()}>
        {index + 1} / {count}
      </div>
    </div>,
    document.body
  );
};

export default MediaViewer;
